import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { ApolloClient } from '@apollo/client'
import {
  AddToWatchListMutation,
  AddToWatchListMutationVariables,
  DeleteFromWatchListMutation,
  DeleteFromWatchListMutationVariables,
  GetContentInWatchListQuery,
  GetContentInWatchListQueryVariables,
  GetDefaultWatchListQuery,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { ADD_TO_WATCHLIST, CONTENT_IN_WATCHLIST, DELETE_FROM_WATCHLIST, GET_DEFAULT_WATCHLIST } from './queries'

export const useGetDefaultWatchlist = ({ client }: { client: ApolloClient<object> }) => {
  const { data, loading } = useQuery<GetDefaultWatchListQuery>(GET_DEFAULT_WATCHLIST, {
    client,
    fetchPolicy: 'network-only',
    context: {
      requireAuth: true,
    },
    errorPolicy: 'all',
    onError: (error) => {
      logger().error('Failed to fetch default watchlist', { error })
    },
  })

  const defaultWatchlist = useMemo(() => {
    try {
      const edges = data?.getMyDefaultWatchList?.items.edges
      if (!edges) {
        return []
      }

      const filteredItems = edges
        .map((item) => item.node.content)
        .filter(
          (item): item is NonNullable<typeof item> & { project: { id: string; slug: string } } => !!item?.project.slug,
        )

      return filteredItems
    } catch (error) {
      logger().error('Error processing default watchlist data', error)
      return []
    }
  }, [data?.getMyDefaultWatchList?.items.edges])

  return { defaultWatchlist, loading }
}

export const useIsTitleInWatchlist = ({ titleId, client }: { titleId: string; client: ApolloClient<object> }) => {
  const { data, refetch } = useQuery<GetContentInWatchListQuery, GetContentInWatchListQueryVariables>(
    CONTENT_IN_WATCHLIST,
    {
      client,
      variables: { contentId: titleId },
      fetchPolicy: 'cache-and-network',
      context: {
        requireAuth: true,
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      skip: !titleId,
      onError: (error) => {
        if (error.message?.includes('duplicate key value violates unique constraint')) {
          logger().warn('Watchlist duplicate key constraint violation - retrying', {
            titleId,
            error: error.message,
          })
          setTimeout(() => {
            refetch().catch((retryError) => {
              logger().error('Failed to retry watchlist query after duplicate key error', {
                titleId,
                error: retryError,
              })
            })
          }, 1000)
        } else {
          logger().error('Error checking watchlist item existence', { titleId, error })
        }
      },
    },
  )

  const isInWatchlist = useMemo(() => {
    try {
      if (!data) {
        return false
      }
      return !!data?.contentInWatchList?.id
    } catch (error) {
      logger().error(
        'Error processing watchlist status',
        {
          titleId,
        },
        error,
      )
      return false
    }
  }, [data, titleId])

  return { isInWatchlist, refetch }
}

export const useEditWatchlist = ({ client }: { client: ApolloClient<object> }) => {
  const [addToList, { loading: addToListLoading }] = useMutation<
    AddToWatchListMutation,
    AddToWatchListMutationVariables
  >(ADD_TO_WATCHLIST, {
    client,
    refetchQueries: [{ query: GET_DEFAULT_WATCHLIST }],
    awaitRefetchQueries: true,
    context: {
      requireAuth: true,
    },
    onCompleted: (data) => {
      logger().info('Successfully added item to watchlist', {
        watchlistId: data?.addToWatchList?.id,
      })
    },
    onError: (error) => {
      logger().error('Failed to add item to watchlist', { error })
    },
  })

  const [deleteFromList, { loading: deleteFromListLoading }] = useMutation<
    DeleteFromWatchListMutation,
    DeleteFromWatchListMutationVariables
  >(DELETE_FROM_WATCHLIST, {
    client,
    refetchQueries: [{ query: GET_DEFAULT_WATCHLIST }],
    awaitRefetchQueries: true,
    context: {
      requireAuth: true,
    },
    errorPolicy: 'all',
    onError: (error) => {
      logger().error('Failed to remove item from watchlist', error)
    },
  })

  const addToWatchlist = useCallback(
    async (contentId: string) => {
      try {
        const result = await addToList({
          client,
          variables: { contentId },
          errorPolicy: 'all',
          context: { requireAuth: true },
        })
        return result
      } catch (error) {
        logger().error('Error in addToWatchlist callback', { contentId }, error)
        throw error
      }
    },
    [addToList, client],
  )

  const deleteFromWatchlist = useCallback(
    async (contentId: string) => {
      try {
        const result = await deleteFromList({
          client,
          variables: { contentId },
          errorPolicy: 'all',
          context: { requireAuth: true },
        })
        return result
      } catch (error) {
        logger().error('Error in deleteFromWatchlist callback', { contentId }, error)
        throw error
      }
    },
    [client, deleteFromList],
  )

  return { addToWatchlist, deleteFromWatchlist, addToListLoading, deleteFromListLoading }
}

export const WATCHLIST_COLLECTION_IDS = new Set<string>([
  '93feab6e-13af-4af6-9f90-19c4616bbdf5',
  '88326055-54dc-48ab-ac44-89d26727d908',
  '62f16897-1250-4ebd-be52-8c7c0b89061f',
  '6625e553-a111-4272-aef4-d6b841c9ffca',
  '872a07f0-886c-4a3a-ad9e-47e005f54b27',
  'd3bcbec0-bff6-4a65-acf7-b5fe3f2f415f',
])

export const WATCHLIST_STAGING_COLLECTION_IDS = new Set<string>([
  'ff370586-8d2d-4fb4-98d1-e24365b6c586',
  'e91b584d-4f96-4fbc-912f-5dc0bd529cfc',
  '7575e9fa-1abf-47dc-b9a4-aa5afcd342bf',
])
